<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :base-z-index="1300"
    :closable="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '26rem' }"
    :breakpoints="{ '960px': '90vw', '575px': '98vw' }"
    :pt="crcFormDialogPt"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div
          class="crc-form-dialog-header__icon bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
        >
          <i class="pi pi-bookmark text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">
            {{ isEdit ? 'Edit Termination Reason' : 'Add Termination Reason' }}
          </div>
        </div>
      </div>
    </template>

    <form @submit.prevent="handleSubmit">
      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-fields">
          <FloatLabel variant="on" class="w-full">
            <Select
              inputId="trm-category"
              v-model="draft.category"
              :options="categoryOptions"
              :disabled="saving"
              class="w-full"
              inputClass="w-full rounded-md"
            />
            <label for="trm-category" class="text-sm font-medium">Category</label>
          </FloatLabel>

          <FloatLabel variant="on" class="w-full">
            <InputText
              id="trm-label"
              v-model="draft.label"
              :disabled="saving"
              maxlength="190"
              autocomplete="off"
              :invalid="submitted && !draft.label?.trim()"
              class="w-full rounded-md"
              placeholder="e.g. Negligence / low performance"
            />
            <label for="trm-label" class="text-sm font-medium">
              Label <span class="text-red-500">*</span>
            </label>
          </FloatLabel>
          <small v-if="submitted && !draft.label?.trim()" class="text-xs text-red-500 pl-1">
            Label is required.
          </small>

          <FloatLabel variant="on" class="w-full">
            <InputNumber
              inputId="trm-sort"
              v-model="draft.sortOrder"
              :disabled="saving"
              :useGrouping="false"
              class="w-full"
              inputClass="w-full rounded-md"
            />
            <label for="trm-sort" class="text-sm font-medium">Sort order</label>
          </FloatLabel>
        </div>
      </section>

      <section v-if="isEdit" class="crc-form-dialog-section">
        <div class="flex items-center justify-between gap-3">
          <div>
            <div class="text-sm font-medium">Active</div>
            <div class="text-xs text-surface-500">Inactive reasons are hidden from terminate dialogs</div>
          </div>
          <ToggleSwitch v-model="draft.active" :disabled="saving" />
        </div>
      </section>

      <div class="crc-form-dialog-footer">
        <div class="crc-form-dialog-footer__actions">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="visibleProxy = false"
          />
          <Button type="submit" label="Save" icon="pi pi-check" :loading="saving" />
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import FloatLabel from 'primevue/floatlabel';
import ToggleSwitch from 'primevue/toggleswitch';
import { crcFormDialogPt } from '@/components/common/dialog/crcDialogPt.js';

defineOptions({ name: 'TermReasonEditModal' });

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  saving: {
    type: Boolean,
    default: false
  },
  categories: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:visible', 'save']);

const submitted = ref(false);

const draft = reactive({
  reasonId: '',
  category: 'Other',
  label: '',
  sortOrder: null,
  active: true
});

const isEdit = computed(() => !!draft.reasonId);

const categoryOptions = computed(() =>
  props.categories.length
    ? props.categories
    : [
        'Personal reason',
        'Truck issues',
        'Documents',
        'Not meeting company standards',
        'Medical leave',
        'Other'
      ]
);

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
});

function resetDraft() {
  draft.reasonId = '';
  draft.category = 'Other';
  draft.label = '';
  draft.sortOrder = null;
  draft.active = true;
  submitted.value = false;
}

function openCreate() {
  resetDraft();
  visibleProxy.value = true;
}

function openEdit(reason) {
  resetDraft();
  draft.reasonId = reason?.reasonId || '';
  draft.category = reason?.category || 'Other';
  draft.label = reason?.label || '';
  draft.sortOrder = reason?.sortOrder ?? null;
  draft.active = reason?.active !== false;
  visibleProxy.value = true;
}

function onClose() {
  resetDraft();
}

function handleSubmit() {
  submitted.value = true;
  if (!draft.label?.trim()) return;

  const payload = {
    reasonId: draft.reasonId || null,
    category: draft.category || 'Other',
    label: draft.label.trim()
  };
  if (draft.sortOrder != null) {
    payload.sortOrder = draft.sortOrder;
  }
  if (isEdit.value) {
    payload.active = !!draft.active;
  }
  emit('save', payload);
}

defineExpose({ openCreate, openEdit });
</script>
